import { Children, isValidElement, useState } from 'react';

import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import CriticalChangesPopup from '@/shared-components/forms/add-artwork/components/CriticalChangesPopup';
import { editArtwork } from '@/shared-components/forms/add-artwork/submit-button/components/EditArtworkHandler.jsx';
import { hasCriticalChanges } from '@/shared-components/forms/add-artwork/utils';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import SaveDraft from './SaveDraft';

import './styles/ArtworkConfirmationDialog.css';

export default function ArtworkConfirmationDialog({
  open,
  onClose, // ✅ Ensure this function is correctly passed and used
  children,
  onConfirm, // ✅ Triggered after saving successfully
  confirmText,
  formState,
  setFormState,
  initialState,
  previewMode,
  editMode,
  deleteDraftMode, 
  setFormError, // ✅ Used for error handling in `editArtwork`
  navigate, // ✅ Added for centralized navigation in `editArtwork`
  cacheHandler, // ✅ Added for cache handling in `editArtwork`
  stateHandler,
}) {
  const [saveExitLoading, setSaveExitLoading] = useState(false);
  const [criticalOpen, setCriticalOpen] = useState(false);
  const goBackHandler = useGoBack("/");
  const { state } = useStateHandler();
  const userType = state?.user_session?.user_type;
  const childrenArray = Children.toArray(children);
  const headerChildIndex = childrenArray.findIndex((child) => (
    isValidElement(child)
    && typeof child.props.className === 'string'
    && child.props.className.split(' ').includes('add-artwork-form__confirmation-text-header')
  ));
  const headerChild = headerChildIndex >= 0 ? childrenArray[headerChildIndex] : null;
  const bodyChildren = headerChildIndex >= 0
    ? childrenArray.filter((_, index) => index !== headerChildIndex)
    : childrenArray;

  /**
   * ✅ Handles the "Save and Exit" logic
   * - Calls `editArtwork()` to save changes
   * - Closes dialog on success
   * - Handles errors properly
   */
  const onSaveAndExitClick = async () => {
    // Si hay cambios críticos, detenemos aquí y mostramos popup
    if (hasCriticalChanges(initialState.current, formState)) {
      if (typeof onClose === 'function') onClose();  // ✅ cierra el popup padre
      setCriticalOpen(true);
      return;
    }

    setSaveExitLoading(true);

    const success = await editArtwork({
      formState, 
      setFormError, 
      navigate, 
      cacheHandler, 
      goBackHandler,
      onArtworkEdited: () => {
        stateHandler.set("temporalPopup", {
          text: "Artwork edited successfully!",
          type: "navbar-present",
        });
      },
    });

    setSaveExitLoading(false);

    if (success) {
      // ✅ Call onConfirm() to handle any exit logic
      if (typeof onConfirm === 'function') {
        onConfirm();
      }
  
      // ✅ Ensure onClose() is properly called after saving
      if (typeof onClose === 'function') {
        onClose();
      }
    } else {      
      // ✅ Close the popup even if there's an error
      if (typeof onClose === 'function') {
        onClose();
      }
    }
  };

  /* ----- acciones del popup de críticos ----- */
  const handleSendReview = async () => {
    setSaveExitLoading(true);
    const success = await editArtwork({
      formState,
      setFormError,
      navigate,
      cacheHandler,
      goBackHandler,
      onArtworkEdited: () => {
        stateHandler.set("temporalPopup", {
          text: "Artwork edited successfully!",
          type: "navbar-present",
        });
      },
    });
    setCriticalOpen(false);
    if (success && typeof onConfirm === 'function') onConfirm();
  };

  const handleRevert = () => {
    if (typeof onClose === 'function') onClose();
    goBackHandler();
  };

  return (
    <>
      <GenericPopup className="artwork-confirmation-dialog remove-scrollbar" open={open} opacity>
        <div className="artwork-confirmation-dialog-popup__header">
          {headerChild}
          <XIcon className="artwork-confirmation-dialog-popup__close" onClick={() => {
            if (typeof onClose === "function") {
              onClose();
            } else {
              console.warn("⚠️ onClose is not defined.");
            }
          }} 
          />
        </div>

        {bodyChildren}

        <div className="artwork-confirmation-dialog__action-buttons element-non-selectable">
          {/* Conditionally render "Save and Exit" button based on editMode */}
          {editMode && (
            <>
              <ContinueButton loading={saveExitLoading}
                onClick={onSaveAndExitClick}
                className={'artwork-confirmation-dialog__save-exit-button'} >
                Save and exit
              </ContinueButton>
            </>
          )}
                    
          {/* ✅ "Yes, Exit" Button (Triggers both onConfirm and onClose) */}
          <ContinueButton
            onClick={() => {
              if (typeof onConfirm === 'function') {
                onConfirm();
              }
              if (typeof onClose === 'function') {
                onClose();
              } else {
                console.warn("❌ onClose is not defined.");
              }
            }}
            className="artwork-confirmation-dialog__yes-exit-button">
            {confirmText}
          </ContinueButton>

          {/* ✅ Save Draft Button (Only shown in non-edit/non-preview mode) */}
          {(!previewMode && !editMode && !deleteDraftMode) && 
            <SaveDraft 
              formState={formState} 
              setFormState={setFormState} 
              initialState={initialState}
              buttonFormat={true}
              onClose={goBackHandler}
            />
          }
        </div>
      </GenericPopup>
      <CriticalChangesPopup
        open={criticalOpen}
        loading={saveExitLoading}
        onSendReview={handleSendReview}
        onRevert={handleRevert}
        onClose={() => setCriticalOpen(false)}
        userType={userType}
      />
    </>
  );
}
